/**
 * TV Navigation Map Types and Interfaces
 * Defines the structure for navigation maps that describe all focusable elements
 * and their spatial relationships for TV remote navigation
 */

import { RefObject } from 'react';

/**
 * Types of focusable elements in the navigation system
 */
export type NavigationNodeType = 'button' | 'input' | 'card' | 'row' | 'container' | 'video';

/**
 * Navigation zones on a page
 */
export type NavigationZone = 'header' | 'hero' | 'content' | 'modal' | 'video' | 'search';

/**
 * Direction for navigation
 */
export type NavigationDirection = 'up' | 'down' | 'left' | 'right';

/**
 * Represents a single focusable element in the navigation map
 */
export interface NavigationNode {
  /** Unique identifier for this node */
  id: string;
  
  /** Type of node */
  type: NavigationNodeType;
  
  /** Component name (for reference and debugging) */
  component: string;
  
  /** Position information */
  position: {
    zone: NavigationZone;
    rowIndex?: number;        // For movie rows
    columnIndex?: number;     // For cards in rows or buttons
  };
  
  /** Neighbor nodes - where focus moves on arrow key press */
  neighbors: {
    up?: string;              // Node ID that should receive focus on Up
    down?: string;            // Node ID that should receive focus on Down
    left?: string;            // Node ID that should receive focus on Left
    right?: string;           // Node ID that should receive focus on Right
  };
  
  /** React ref to the DOM element (set at runtime) */
  ref?: RefObject<HTMLElement>;
  
  /** Additional metadata for the node */
  metadata?: {
    rowId?: string;            // For movie rows and cards
    totalCards?: number;      // For rows
    cardIndex?: number;       // For cards
    isDynamic?: boolean;       // Whether this node is dynamically generated
  };
}

/**
 * Navigation rule for special cases
 */
export interface NavigationRule {
  /** Rule identifier */
  id: string;
  
  /** Condition that triggers this rule */
  condition: (node: NavigationNode, direction: NavigationDirection) => boolean;
  
  /** Action to take when rule is triggered */
  action: (node: NavigationNode, direction: NavigationDirection) => string | null;
}

/**
 * Navigation map for a single page
 */
export interface PageNavigationMap {
  /** Page identifier (e.g., 'home', 'watch', 'details') */
  pageId: string;
  
  /** Zones containing nodes */
  zones: {
    [zoneId: string]: {
      nodes: NavigationNode[];
      defaultFocus?: string;  // Default node to focus on page load
    };
  };
  
  /** Optional navigation rules for special cases */
  navigationRules?: {
    [ruleId: string]: NavigationRule;
  };
}

/**
 * Helper function to find a node by ID in a navigation map
 */
export function findNodeInMap(map: PageNavigationMap, nodeId: string): NavigationNode | null {
  for (const zone of Object.values(map.zones)) {
    const node = zone.nodes.find(n => n.id === nodeId);
    if (node) return node;
  }
  return null;
}

/**
 * Helper function to find all nodes of a specific type
 */
export function findNodesByType(map: PageNavigationMap, type: NavigationNodeType): NavigationNode[] {
  const nodes: NavigationNode[] = [];
  for (const zone of Object.values(map.zones)) {
    nodes.push(...zone.nodes.filter(n => n.type === type));
  }
  return nodes;
}

/**
 * Helper function to find all nodes in a specific zone
 */
export function findNodesByZone(map: PageNavigationMap, zone: NavigationZone): NavigationNode[] {
  const zoneData = map.zones[zone];
  return zoneData ? zoneData.nodes : [];
}

/**
 * Helper function to generate card nodes for a movie row
 */
export function generateCardNodes(
  rowId: string,
  rowIndex: number,
  totalCards: number,
  previousRowId?: string,
  nextRowId?: string,
  upTarget?: string
): NavigationNode[] {
  return Array.from({ length: totalCards }, (_, cardIndex) => ({
    id: `${rowId}-card-${cardIndex}`,
    type: 'card',
    component: 'MovieCard',
    position: {
      zone: 'content',
      rowIndex,
      columnIndex: cardIndex
    },
    neighbors: {
      left: cardIndex > 0 ? `${rowId}-card-${cardIndex - 1}` : undefined,
      right: cardIndex < totalCards - 1 ? `${rowId}-card-${cardIndex + 1}` : undefined,
      up: previousRowId 
        ? `${previousRowId}-card-${Math.min(cardIndex, totalCards - 1)}` 
        : upTarget,
      down: nextRowId 
        ? `${nextRowId}-card-${Math.min(cardIndex, totalCards - 1)}` 
        : undefined
    },
    metadata: {
      rowId,
      cardIndex,
      isDynamic: true
    }
  }));
}


